import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Heart, Sparkles } from 'lucide-react';
import mockData from '../mock';

const Footer = () => {
  return (
    <footer className="bg-gray-950 border-t border-violet-500/20 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-r from-violet-500/5 to-purple-500/5"></div>
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-violet-500 to-purple-500"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="space-y-6">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="relative">
                {/* Chatbot Logo Image - Made Larger, No Border/Glow */}
                <div className="relative w-16 h-16 rounded-xl overflow-hidden transition-all duration-300">
                  <img 
                    src="/logo.jpeg" 
                    alt="BrixBot Logo" 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Fallback to gradient background if image fails to load
                      e.target.style.display = 'none';
                      e.target.parentElement.style.background = 'linear-gradient(135deg, #8b5cf6, #a855f7)';
                      e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center text-white font-bold text-2xl">B</div>';
                    }}
                  />
                </div>
              </div>
              
              {/* BrixBot Gradient Text */}
              <div className="relative">
                <span className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-purple-400 to-fuchsia-400 group-hover:from-violet-300 group-hover:via-purple-300 group-hover:to-fuchsia-300 transition-all duration-300">
                  BrixBot
                </span>
                <div className="absolute -inset-1 bg-gradient-to-r from-violet-400 to-purple-400 blur opacity-20 group-hover:opacity-30 transition-all duration-300 rounded-lg"></div>
              </div>
            </Link>
            
            <p className="text-gray-400 text-sm leading-relaxed">
              {mockData.company.description}
            </p>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3 className="text-white font-semibold text-lg flex items-center">
              <Sparkles className="w-5 h-5 mr-2 text-violet-400" />
              Quick Links
            </h3>
            <ul className="space-y-3">
              {mockData.navigation.map((item) => (
                <li key={item.name}>
                  <Link 
                    to={item.href}
                    className="text-gray-400 hover:text-violet-400 transition-colors text-sm relative group"
                  >
                    {item.name}
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-violet-500 to-purple-500 transition-all duration-300 group-hover:w-full"></span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="space-y-4">
            <h3 className="text-white font-semibold text-lg">Services</h3>
            <ul className="space-y-3 text-sm">
              <li>
                <a href="#" className="text-gray-400 hover:text-violet-400 transition-colors relative group">
                  Custom AI Training
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-violet-500 to-purple-500 transition-all duration-300 group-hover:w-full"></span>
                </a>
              </li>
              <li>
                <a href="#" className="text-gray-400 hover:text-violet-400 transition-colors relative group">
                  Integration Support
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-violet-500 to-purple-500 transition-all duration-300 group-hover:w-full"></span>
                </a>
              </li>
              <li>
                <a href="#" className="text-gray-400 hover:text-violet-400 transition-colors relative group">
                  Analytics & Reporting
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-violet-500 to-purple-500 transition-all duration-300 group-hover:w-full"></span>
                </a>
              </li>
              <li>
                <a href="#" className="text-gray-400 hover:text-violet-400 transition-colors relative group">
                  24/7 Technical Support
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-violet-500 to-purple-500 transition-all duration-300 group-hover:w-full"></span>
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h3 className="text-white font-semibold text-lg">Contact</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center space-x-3 group">
                <div className="p-2 bg-violet-500/10 rounded-lg group-hover:bg-violet-500/20 transition-all duration-300">
                  <Mail className="w-4 h-4 text-violet-400" />
                </div>
                <span className="text-gray-400 group-hover:text-violet-300 transition-colors">support@brixbot.ai</span>
              </div>
              <div className="flex items-center space-x-3 group">
                <div className="p-2 bg-violet-500/10 rounded-lg group-hover:bg-violet-500/20 transition-all duration-300">
                  <Phone className="w-4 h-4 text-violet-400" />
                </div>
                <span className="text-gray-400 group-hover:text-violet-300 transition-colors">+421915721942</span>
              </div>
              <div className="flex items-center space-x-3 group">
                <div className="p-2 bg-violet-500/10 rounded-lg group-hover:bg-violet-500/20 transition-all duration-300">
                  <MapPin className="w-4 h-4 text-violet-400" />
                </div>
                <span className="text-gray-400 group-hover:text-violet-300 transition-colors">Žilina, Slovakia</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-violet-500/20 flex flex-col md:flex-row justify-between items-center">
          <div className="text-sm text-gray-400 flex items-center">
            © 2025 BrixBot LLC. Made with 
            <Heart className="w-4 h-4 mx-1 text-violet-400 animate-pulse" />
            in Slovakia.
          </div>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <Link to="/privacy-policy" className="text-sm text-gray-400 hover:text-violet-400 transition-colors relative group">
              Privacy Policy
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-violet-500 to-purple-500 transition-all duration-300 group-hover:w-full"></span>
            </Link>
            <Link to="/terms-of-service" className="text-sm text-gray-400 hover:text-violet-400 transition-colors relative group">
              Terms of Service
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-violet-500 to-purple-500 transition-all duration-300 group-hover:w-full"></span>
            </Link>
            <Link to="/cookie-policy" className="text-sm text-gray-400 hover:text-violet-400 transition-colors relative group">
              Cookie Policy
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-violet-500 to-purple-500 transition-all duration-300 group-hover:w-full"></span>
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;